/**
 * voices.ts — the VOICE ALLOCATOR's policy (2026-09-20). PURE: it decides, the
 * runtime renders. No Web Audio here, so the harness can throw random bursts at
 * it and prove the limits hold.
 *
 * Before this the SFX bus had NO cap at all ("the cooldown is the only guard"):
 * a battle grew the audio workload without bound, and the engine's zone pool
 * stole by AGE alone — the oldest voice, which is exactly the loop you wanted
 * to keep. Admission now answers four questions, in order:
 *
 *   1. one SOUND   — a key may hold only so many instances; the oldest yields
 *                    (voice limiting per sound)
 *   2. one CATEGORY— a bus has a ceiling (a battle cannot starve the UI)
 *   3. the WORLD   — a global ceiling from the quality tier
 *   4. who yields  — the lowest SCORE, never a bus at its protected minimum,
 *                    and only to a challenger that beats it by a margin
 *                    (hysteresis: two equals do not swap a slot every frame)
 *
 * A RELEASING voice (its 12 ms steal fade) still COUNTS — there is no hidden +1.
 */
import { BUS_SPEC, busCap, keyInstanceCap, type AudioBudget, type Bus } from './buses.js';

export interface LiveVoice {
  id: number;
  key: string;
  bus: Bus;
  priority: number;
  /** Estimated audible gain 0..1 at admission (caller gain × distance). */
  gain: number;
  loop: boolean;
  startedAt: number;
  /** Expected natural end (seconds, the allocator's clock); Infinity for a loop. */
  endsAt: number;
  /** Set while the steal fade runs: the voice is going, but it still holds its slot. */
  releasing: boolean;
}

export interface AdmitRequest { key: string; bus: Bus; priority: number; gain: number; loop: boolean; durationS: number }

export type DropReason = 'key-cap' | 'bus-cap' | 'global-cap';
export type Admission =
  | { kind: 'start' }
  | { kind: 'steal'; victimId: number }
  | { kind: 'drop'; reason: DropReason };

/** A challenger must beat its victim by this much (the report's proposed 15). */
export const STEAL_MARGIN = 15;
/** The steal fade — long enough not to click, short enough to stay responsive. */
export const STEAL_FADE_S = 0.012;

/**
 * What a voice is WORTH right now: authored importance first, then how audible
 * it is; a one-shot near its end is cheap to take (its tail is mostly gone).
 */
export function voiceScore(v: Pick<LiveVoice, 'priority' | 'gain' | 'loop' | 'startedAt' | 'endsAt'>, now: number): number {
  const span = v.endsAt - v.startedAt;
  const progress = v.loop || !Number.isFinite(span) || span <= 0 ? 0 : Math.min(1, Math.max(0, (now - v.startedAt) / span));
  return v.priority + 25 * Math.min(1, Math.max(0, v.gain)) - 12 * progress;
}

export class VoiceAllocator {
  private readonly live = new Map<number, LiveVoice>();
  private nextId = 1;
  readonly stats = { started: 0, stolen: 0, dropped: { 'key-cap': 0, 'bus-cap': 0, 'global-cap': 0 } as Record<DropReason, number>, peak: 0 };

  constructor(private budget: AudioBudget) {}

  setBudget(b: AudioBudget): void { this.budget = b; }
  get cap(): number { return this.budget.voices; }
  get size(): number { return this.live.size; }
  countOf(bus: Bus): number { let n = 0; for (const v of this.live.values()) if (v.bus === bus) n++; return n; }
  voices(): IterableIterator<LiveVoice> { return this.live.values(); }

  /**
   * Decide. `start` and `steal` both mean "the newcomer plays"; on `steal` the
   * caller fades `victimId` (then calls `markReleasing`) and may start the
   * newcomer at once — the victim keeps its slot until `release`.
   */
  admit(req: AdmitRequest, now: number): Admission {
    // 1. Voice limiting per sound: the OLDEST instance of the same key yields (no margin — same sound, same worth).
    const keyCap = keyInstanceCap(req.key);
    let sameKey = 0;
    let oldestSame: LiveVoice | null = null;
    for (const v of this.live.values()) {
      if (v.key !== req.key || v.releasing) continue;
      sameKey++;
      if (!oldestSame || v.startedAt < oldestSame.startedAt || (v.startedAt === oldestSame.startedAt && v.id < oldestSame.id)) oldestSame = v;
    }
    if (sameKey >= keyCap) {
      if (!oldestSame || oldestSame.loop) return this.drop('key-cap');
      return { kind: 'steal', victimId: oldestSame.id };
    }
    const challenger = voiceScore({ priority: req.priority, gain: req.gain, loop: req.loop, startedAt: now, endsAt: now + req.durationS }, now);
    // 2. The category ceiling: the victim comes from the SAME bus.
    if (this.countOf(req.bus) >= busCap(req.bus, this.budget)) {
      const victim = this.lowest((v) => v.bus === req.bus, now);
      if (!victim || challenger < voiceScore(victim, now) + STEAL_MARGIN) return this.drop('bus-cap');
      return { kind: 'steal', victimId: victim.id };
    }
    // 3. The global ceiling: any bus ABOVE its protected minimum may yield.
    if (this.live.size >= this.budget.voices) {
      const counts = new Map<Bus, number>();
      for (const v of this.live.values()) counts.set(v.bus, (counts.get(v.bus) ?? 0) + 1);
      const victim = this.lowest((v) => v.bus === req.bus || (counts.get(v.bus) ?? 0) > BUS_SPEC[v.bus].reserve, now);
      if (!victim || challenger < voiceScore(victim, now) + STEAL_MARGIN) return this.drop('global-cap');
      return { kind: 'steal', victimId: victim.id };
    }
    return { kind: 'start' };
  }

  /** Register a started voice; returns its id. */
  add(req: AdmitRequest, now: number): number {
    const id = this.nextId++;
    this.live.set(id, { id, key: req.key, bus: req.bus, priority: req.priority, gain: req.gain, loop: req.loop, startedAt: now, endsAt: req.loop ? Infinity : now + req.durationS, releasing: false });
    this.stats.started++;
    if (this.live.size > this.stats.peak) this.stats.peak = this.live.size;
    return id;
  }

  markReleasing(id: number): void { const v = this.live.get(id); if (v) { v.releasing = true; this.stats.stolen++; } }
  release(id: number): void { this.live.delete(id); }
  setGain(id: number, gain: number): void { const v = this.live.get(id); if (v) v.gain = gain; }

  private drop(reason: DropReason): Admission { this.stats.dropped[reason]++; return { kind: 'drop', reason }; }

  /** The cheapest stealable voice: never one already releasing; ties break old-first, then by id (deterministic). */
  private lowest(eligible: (v: LiveVoice) => boolean, now: number): LiveVoice | null {
    let best: LiveVoice | null = null;
    let bestScore = Infinity;
    for (const v of this.live.values()) {
      if (v.releasing || !eligible(v)) continue;
      const s = voiceScore(v, now);
      if (s < bestScore || (s === bestScore && best !== null && (v.startedAt < best.startedAt || (v.startedAt === best.startedAt && v.id < best.id)))) { best = v; bestScore = s; }
    }
    return best;
  }
}
